import React from 'react';
import { Alert, Button, Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../../services/AxiosInstance';
import NewPage from '../../../layouts/page/NewPage';
import InputText from '../../../components/Forms/Element/InputText';
import CustomSelect from '../../../components/Forms/Element/CustomSelect';
import AsteriskRequired from '../../../components/Common/AsteriskRequired';
import StatusCheckBox from '../../../components/Common/StatusCheckbox';
import MenuConfig from '../../../../constants/MenuConfig';
import Api from '../../../../constants/Api';
import CompanyProvisionService from '../../../../services/superadmin/CompanyProvisionService';
import CompanyCategory, { DEFAULT_COMPANY_CATEGORY } from '../../../../constants/CompanyCategory';
import { errorAlert, successAlert, warnAlert } from '../../../components/alert/defaultAlert';
import { convertBooleanToStatus } from '../../../../utils/ConverterUtils';
import FormSection from '../../../components/Common/Form/FormSection';
import CnpjInput from '../../../components/Common/CnpjInput';
import CpfCnpjInput from '../../../components/Common/CpfCnpjInput';
import AddressRegistration from '../../../components/Custom/Common/Address/AddressRegistration';
import { fillFormFromCnpjLookup, normalizeCnpjDigits } from '../../../../utils/CnpjLookupUtils';
import './CompanyProvisionNew.css';

// Espelha a regra do backend para o usuário já ver o slug final no formulário.
const normalizePublicSlug = (value, preserveTrailingHyphen = false) => {
    let slug = (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+/, '')
        .replace(/-{2,}/g, '-');

    // Mantém o hífen final enquanto a pessoa ainda está compondo o próximo trecho do slug.
    if (!preserveTrailingHyphen) {
        slug = slug.replace(/-+$/, '');
    }
    return slug;
};

const PUBLIC_INTRO_IMAGE_MAX_SIZE = 5 * 1024 * 1024;
const PUBLIC_INTRO_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const getPublicIntroImageEndpoint = (companyId, stepNumber) =>
    `${Api.registration.company_superadmin}/${companyId}/public-page/images/${stepNumber}`;

const getPublicIntroStepKey = (stepNumber) => (stepNumber === 1 ? 'One' : stepNumber === 2 ? 'Two' : 'Three');

const CompanyProvisionNew = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = id !== undefined;
    const categoryOptions = Object.values(CompanyCategory).map((category) => ({
        value: category,
        label: t(category.toLowerCase()),
    }));
    // Segue o padrão dos outros cadastros: marcado = ativa. Empresa nova nasce ativa.
    const [status, setStatus] = React.useState(true);
    const [entityData, setEntityData] = React.useState(null);
    const [publicIntroImageFiles, setPublicIntroImageFiles] = React.useState({ 1: null, 2: null, 3: null });
    const [publicIntroImagePreviews, setPublicIntroImagePreviews] = React.useState({ 1: '', 2: '', 3: '' });
    const [isLookingUpCnpj, setIsLookingUpCnpj] = React.useState(false);
    const publicIntroImageInputRefs = {
        1: React.useRef(null),
        2: React.useRef(null),
        3: React.useRef(null),
    };

    const formik = useFormik({
        initialValues: {
            name: '',
            corporateName: '',
            category: DEFAULT_COMPANY_CATEGORY,
            cnpj: '',
            phone: '',
            responsibleName: '',
            trialDays: '',
            publicSlug: '',
            publicIntroStepCount: 0,
            publicIntroStepOneTitle: '',
            publicIntroStepOneText: '',
            publicIntroStepOneImageUrl: '',
            publicIntroStepTwoTitle: '',
            publicIntroStepTwoText: '',
            publicIntroStepTwoImageUrl: '',
            publicIntroStepThreeTitle: '',
            publicIntroStepThreeText: '',
            publicIntroStepThreeImageUrl: '',
            adminFirstName: '',
            adminLastName: '',
            adminEmail: '',
            firstBranchName: '',
            firstBranchDocument: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            zipCode: '',
        },
        validationSchema: Yup.object().shape({
            name: Yup.string()
                .min(3, t('validation.min.generic', { field: t('trade_name'), qty: 3 }))
                .required(t('superadmin_company.validation.name_required')),
            corporateName: Yup.string()
                .min(3, t('validation.min.generic', { field: t('corporate_name'), qty: 3 }))
                .required(t('superadmin_company.validation.corporate_name_required')),
            // Aceita CPF ou CNPJ: o tamanho distingue os dois, e o servidor refaz a
            // conferência dos dígitos verificadores (CompanyService#save).
            cnpj: Yup.string()
                .required(t('validation.required.document'))
                .test('cpf-ou-cnpj', t('validation.invalid.document'), valor => {
                    const digitos = String(valor ?? '').replace(/\D/g, '');
                    return digitos.length === 11 || digitos.length === 14;
                }),
            category: Yup.string()
                .oneOf(Object.values(CompanyCategory), t('superadmin_company.validation.category_required'))
                .required(t('superadmin_company.validation.category_required')),
            phone: Yup.string().nullable().optional(),
            responsibleName: Yup.string().max(100, 'Máximo de 100 caracteres.').nullable().optional(),

            // O slug vira parte da URL /p/{slug}; quando não informado, o backend gera a partir do nome.
            publicSlug: Yup.string()
                .min(3, 'Informe pelo menos 3 caracteres.')
                .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use apenas letras minúsculas, números e hífen.')
                .nullable()
                .optional(),
            // O superadmin define se a intro terá fluxo curto ou completo.
            publicIntroStepCount: Yup.number()
                .oneOf([0, 1, 2, 3], 'Escolha de 0 a 3 cards.')
                .required('Informe a quantidade de cards.'),
            publicIntroStepOneTitle: Yup.string().max(90, 'Máximo de 90 caracteres.').nullable().optional(),
            publicIntroStepOneText: Yup.string().max(260, 'Máximo de 260 caracteres.').nullable().optional(),
            publicIntroStepOneImageUrl: Yup.string().max(500, 'Máximo de 500 caracteres.').nullable().optional(),
            publicIntroStepTwoTitle: Yup.string().max(90, 'Máximo de 90 caracteres.').nullable().optional(),
            publicIntroStepTwoText: Yup.string().max(260, 'Máximo de 260 caracteres.').nullable().optional(),
            publicIntroStepTwoImageUrl: Yup.string().max(500, 'Máximo de 500 caracteres.').nullable().optional(),
            publicIntroStepThreeTitle: Yup.string().max(90, 'Máximo de 90 caracteres.').nullable().optional(),
            publicIntroStepThreeText: Yup.string().max(260, 'Máximo de 260 caracteres.').nullable().optional(),
            publicIntroStepThreeImageUrl: Yup.string().max(500, 'Máximo de 500 caracteres.').nullable().optional(),
            trialDays: Yup.number()
                .integer(t('validation.invalid.generic'))
                .min(0, t('validation.invalid.generic'))
                .nullable()
                .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
            adminFirstName: Yup.string()
                .min(3, t('validation.min.generic', { field: t('name'), qty: 3 }))
                .when([], {
                    is: () => !isEditMode,
                    then: (schema) => schema.required(t('validation.required.firstName')),
                    otherwise: (schema) => schema.optional(),
                }),
            adminLastName: Yup.string()
                .min(3, t('validation.min.generic', { field: t('name'), qty: 3 }))
                .when([], {
                    is: () => !isEditMode,
                    then: (schema) => schema.required(t('validation.required.lastName')),
                    otherwise: (schema) => schema.optional(),
                }),
            adminEmail: Yup.string()
                .email(t('validation.invalid.email'))
                .when([], {
                    is: () => !isEditMode,
                    then: (schema) => schema.required(t('validation.required.email')),
                    otherwise: (schema) => schema.optional(),
                }),
            firstBranchName: Yup.string()
                .min(3, t('validation.min.generic', { field: t('branch'), qty: 3 }))
                .when([], {
                    is: () => !isEditMode,
                    then: (schema) => schema.required('Informe o nome da primeira filial.'),
                    otherwise: (schema) => schema.optional(),
                }),
        }),
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            try {
                let savedCompanyId = id;
                if (isEditMode) {
                    await CompanyProvisionService.update(id, {
                        id: entityData?.id,
                        name: values.name.trim(),
                        corporateName: values.corporateName.trim(),
                        category: values.category,
                        cnpj: values.cnpj.trim(),
                        phone: values.phone.trim(),
                        responsibleName: values.responsibleName.trim(),
                        trialDays: values.trialDays === '' ? null : Number(values.trialDays),

                        // Configuração da página pública exibida antes do login/cadastro.
                        publicSlug: values.publicSlug.trim(),
                        publicIntroStepCount: Number(values.publicIntroStepCount),
                        publicIntroStepOneTitle: values.publicIntroStepOneTitle.trim(),
                        publicIntroStepOneText: values.publicIntroStepOneText.trim(),
                        publicIntroStepOneImageUrl: values.publicIntroStepOneImageUrl.trim(),
                        publicIntroStepTwoTitle: values.publicIntroStepTwoTitle.trim(),
                        publicIntroStepTwoText: values.publicIntroStepTwoText.trim(),
                        publicIntroStepTwoImageUrl: values.publicIntroStepTwoImageUrl.trim(),
                        publicIntroStepThreeTitle: values.publicIntroStepThreeTitle.trim(),
                        publicIntroStepThreeText: values.publicIntroStepThreeText.trim(),
                        publicIntroStepThreeImageUrl: values.publicIntroStepThreeImageUrl.trim(),
                        status: convertBooleanToStatus(status),
                    });
                } else {
                    const response = await CompanyProvisionService.create({
                        name: values.name.trim(),
                        corporateName: values.corporateName.trim(),
                        category: values.category,
                        cnpj: values.cnpj.trim(),
                        phone: values.phone.trim(),
                        responsibleName: values.responsibleName.trim(),
                        trialDays: values.trialDays === '' ? null : Number(values.trialDays),

                        // A empresa nova já nasce com o link público e o conteúdo da intro.
                        publicSlug: values.publicSlug.trim(),
                        publicIntroStepCount: Number(values.publicIntroStepCount),
                        publicIntroStepOneTitle: values.publicIntroStepOneTitle.trim(),
                        publicIntroStepOneText: values.publicIntroStepOneText.trim(),
                        publicIntroStepOneImageUrl: values.publicIntroStepOneImageUrl.trim(),
                        publicIntroStepTwoTitle: values.publicIntroStepTwoTitle.trim(),
                        publicIntroStepTwoText: values.publicIntroStepTwoText.trim(),
                        publicIntroStepTwoImageUrl: values.publicIntroStepTwoImageUrl.trim(),
                        publicIntroStepThreeTitle: values.publicIntroStepThreeTitle.trim(),
                        publicIntroStepThreeText: values.publicIntroStepThreeText.trim(),
                        publicIntroStepThreeImageUrl: values.publicIntroStepThreeImageUrl.trim(),
                        status: convertBooleanToStatus(status),
                        adminFirstName: values.adminFirstName.trim(),
                        adminLastName: values.adminLastName.trim(),
                        adminEmail: values.adminEmail.trim(),
                        firstBranch: {
                            name: values.firstBranchName.trim(),
                            document: values.firstBranchDocument.trim(),
                            address: {
                                street: values.street.trim(),
                                number: values.number.trim(),
                                complement: values.complement.trim(),
                                neighborhood: values.neighborhood.trim(),
                                city: values.city.trim(),
                                state: values.state.trim(),
                                zipCode: values.zipCode.trim(),
                            },
                        },
                    });
                    savedCompanyId = response.data.id;
                }

                await uploadPublicIntroImages(savedCompanyId);

                // No cadastro, confirma o destinatário efetivo para facilitar a conferência
                // e evitar que um e-mail digitado incorretamente passe despercebido.
                const successMessage = isEditMode
                    ? t('message.operation_successfully')
                    : t('superadmin_company.success', { email: values.adminEmail.trim() });

                successAlert(successMessage, () => {
                    resetForm();
                    setStatus(true);
                    clearPublicIntroImageInputs();
                    navigate(MenuConfig.superadmin.company.route);
                });
            } catch (error) {
                errorAlert(error?.response?.data?.message || error?.message || t('error'));
            } finally {
                setSubmitting(false);
            }
        }
    });

    React.useEffect(() => {
        if (!entityData) {
            return;
        }

        formik.setValues({
            name: entityData.name || '',
            corporateName: entityData.corporateName || '',
            // A coluna é obrigatória no banco; o padrão só cobre um backend que ainda não tenha o campo.
            category: entityData.category || DEFAULT_COMPANY_CATEGORY,
            cnpj: entityData.cnpj || '',
            phone: entityData.phone || '',
            responsibleName: entityData.responsibleName || '',
            trialDays: entityData.trialDays ?? '',
            publicSlug: entityData.publicSlug || '',
            publicIntroStepCount: entityData.publicIntroStepCount ?? 0,
            publicIntroStepOneTitle: entityData.publicIntroStepOneTitle || '',
            publicIntroStepOneText: entityData.publicIntroStepOneText || '',
            publicIntroStepOneImageUrl: entityData.publicIntroStepOneImageUrl || '',
            publicIntroStepTwoTitle: entityData.publicIntroStepTwoTitle || '',
            publicIntroStepTwoText: entityData.publicIntroStepTwoText || '',
            publicIntroStepTwoImageUrl: entityData.publicIntroStepTwoImageUrl || '',
            publicIntroStepThreeTitle: entityData.publicIntroStepThreeTitle || '',
            publicIntroStepThreeText: entityData.publicIntroStepThreeText || '',
            publicIntroStepThreeImageUrl: entityData.publicIntroStepThreeImageUrl || '',
            adminFirstName: '',
            adminLastName: '',
            adminEmail: '',
            firstBranchName: '',
            firstBranchDocument: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            zipCode: '',
        });
        setStatus(entityData.status !== 'INACTIVE');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityData]);

    const handleSubmit = (event) => {
        event.preventDefault();
        formik.handleSubmit();
    };

    const clearPublicIntroImageInputs = () => {
        setPublicIntroImageFiles({ 1: null, 2: null, 3: null });
        setPublicIntroImagePreviews({ 1: '', 2: '', 3: '' });
        Object.values(publicIntroImageInputRefs).forEach((ref) => {
            if (ref.current) {
                ref.current.value = '';
            }
        });
    };

    const handlePublicSlugChange = (event) => {
        formik.setFieldValue('publicSlug', normalizePublicSlug(event.target.value, true));
    };

    const handlePublicSlugBlur = (event) => {
        formik.handleBlur(event);
        formik.setFieldValue('publicSlug', normalizePublicSlug(event.target.value));
    };

    // Sugere o slug a partir do nome, mas preserva edições manuais do usuário.
    const handleNameBlur = (event) => {
        formik.handleBlur(event);
        if (!formik.values.publicSlug) {
            formik.setFieldValue('publicSlug', normalizePublicSlug(event.target.value));
        }
    };

    // Só há o que consultar quando o documento tem 14 dígitos; com CPF a base de CNPJ não
    // retorna nada e o operador receberia um erro sem entender o motivo.
    const isCnpjInformado = normalizeCnpjDigits(formik.values.cnpj).length === 14;

    const handleLookupCnpj = async () => {
        const normalizedCnpj = normalizeCnpjDigits(formik.values.cnpj);
        if (normalizedCnpj.length !== 14) {
            errorAlert('Informe um CNPJ completo para realizar a consulta.');
            return;
        }

        setIsLookingUpCnpj(true);
        try {
            const response = await CompanyProvisionService.lookupCnpj(normalizedCnpj);
            const statusWarning = fillFormFromCnpjLookup(formik, response.data || {}, normalizedCnpj);
            if (statusWarning) {
                warnAlert(statusWarning);
            }
        } catch (error) {
            errorAlert(error?.response?.data?.message || 'Não foi possível consultar os dados deste CNPJ.');
        } finally {
            setIsLookingUpCnpj(false);
        }
    };

    const getPublicIntroStepCount = () => {
        // Mantém 0 como uma opção válida; usando "|| 3", o zero voltaria para três cards.
        const stepCount = Number(formik.values.publicIntroStepCount);
        return Number.isNaN(stepCount) ? 0 : stepCount;
    };

    const clearInactivePublicIntroSteps = (stepCount) => {
        /*
         * Ao reduzir a quantidade de cards, limpamos os campos que saíram do fluxo
         * para não salvar conteúdo escondido no cadastro da empresa.
         */
        [1, 2, 3].forEach((stepNumber) => {
            if (stepNumber <= stepCount) {
                return;
            }

            const key = getPublicIntroStepKey(stepNumber);
            formik.setFieldValue(`publicIntroStep${key}Title`, '');
            formik.setFieldValue(`publicIntroStep${key}Text`, '');
            formik.setFieldValue(`publicIntroStep${key}ImageUrl`, '');
            formik.setFieldTouched(`publicIntroStep${key}Title`, false);
            formik.setFieldTouched(`publicIntroStep${key}Text`, false);
            formik.setFieldTouched(`publicIntroStep${key}ImageUrl`, false);

            if (publicIntroImagePreviews[stepNumber]?.startsWith('blob:')) {
                URL.revokeObjectURL(publicIntroImagePreviews[stepNumber]);
            }

            setPublicIntroImageFiles((previous) => ({ ...previous, [stepNumber]: null }));
            setPublicIntroImagePreviews((previous) => ({ ...previous, [stepNumber]: '' }));
            if (publicIntroImageInputRefs[stepNumber].current) {
                publicIntroImageInputRefs[stepNumber].current.value = '';
            }
        });
    };

    const handlePublicIntroStepCountChange = (event) => {
        const stepCount = Number(event.target.value);
        formik.setFieldValue('publicIntroStepCount', stepCount);
        clearInactivePublicIntroSteps(stepCount);
    };

    const handlePublicIntroImageChange = (stepNumber, event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        // Validação local evita enviar arquivos incompatíveis para o backend.
        if (!PUBLIC_INTRO_IMAGE_TYPES.includes(file.type)) {
            warnAlert('A imagem deve ser PNG, JPG ou WEBP.');
            event.target.value = '';
            return;
        }

        if (file.size > PUBLIC_INTRO_IMAGE_MAX_SIZE) {
            warnAlert('A imagem deve ter no máximo 5MB.');
            event.target.value = '';
            return;
        }

        if (publicIntroImagePreviews[stepNumber]?.startsWith('blob:')) {
            URL.revokeObjectURL(publicIntroImagePreviews[stepNumber]);
        }

        setPublicIntroImageFiles((previous) => ({ ...previous, [stepNumber]: file }));
        setPublicIntroImagePreviews((previous) => ({ ...previous, [stepNumber]: URL.createObjectURL(file) }));
    };

    const uploadPublicIntroImages = async (companyId) => {
        const activeStepCount = getPublicIntroStepCount();
        const selectedEntries = Object.entries(publicIntroImageFiles)
            .filter(([stepNumber, file]) => Number(stepNumber) <= activeStepCount && Boolean(file));
        if (!companyId || selectedEntries.length === 0) {
            return;
        }

        /*
         * O upload fica separado do PATCH/POST da empresa porque multipart e JSON
         * usam contratos diferentes. Isso também permite trocar só uma imagem.
         */
        for (const [stepNumber, file] of selectedEntries) {
            const formData = new FormData();
            formData.append('file', file);
            await axiosInstance.post(getPublicIntroImageEndpoint(companyId, stepNumber), formData, {
                headers: { 'content-type': 'multipart/form-data' },
            });
        }
    };

    const removePublicIntroImage = async (stepNumber) => {
        if (!isEditMode || !entityData?.id) {
            return;
        }

        try {
            await axiosInstance.delete(getPublicIntroImageEndpoint(entityData.id, stepNumber));
            setEntityData((previous) => ({
                ...previous,
                [`hasPublicIntroStep${stepNumber === 1 ? 'One' : stepNumber === 2 ? 'Two' : 'Three'}Image`]: false,
            }));
            setPublicIntroImageFiles((previous) => ({ ...previous, [stepNumber]: null }));
            setPublicIntroImagePreviews((previous) => ({ ...previous, [stepNumber]: '' }));
            if (publicIntroImageInputRefs[stepNumber].current) {
                publicIntroImageInputRefs[stepNumber].current.value = '';
            }
            successAlert(t('message.data_updated_successfully'));
        } catch (error) {
            errorAlert(error?.response?.data?.message || error?.message || t('error'));
        }
    };

    const getExistingPublicIntroImageUrl = (stepNumber) => {
        if (stepNumber > getPublicIntroStepCount()) {
            return '';
        }

        if (!entityData?.publicSlug) {
            return '';
        }

        const key = getPublicIntroStepKey(stepNumber);
        if (!entityData[`hasPublicIntroStep${key}Image`]) {
            return '';
        }

        return `${process.env.REACT_APP_API_URL}/api/public-pages/${entityData.publicSlug}/images/${stepNumber}`;
    };

    const activePublicIntroStepCount = getPublicIntroStepCount();

    return (
        <NewPage
            motherMenu={t('administration')}
            childMenu={t('superadmin_company.menu')}
            activeMenu={t(isEditMode ? 'update' : 'new')}
            handleSubmit={handleSubmit}
            isSubmitting={formik.isSubmitting}
            cancelRoute={MenuConfig.superadmin.company.route}
            setEntityData={setEntityData}
            path={Api.registration.company_superadmin}
            child={
                <div className="form-layout form-layout--spaced">
                    <FormSection title={t('superadmin_company.company_section')} description={t('superadmin_company.company_hint')} icon="fa-solid fa-building">

                        <Row className="g-3">
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>{t('cpf_cnpj')} <AsteriskRequired /></FormLabel>
                                    <div className="d-flex gap-2 align-items-start">
                                        <div className="flex-grow-1">
                                            <CpfCnpjInput
                                                name="cnpj"
                                                placeholder={t('superadmin_company.placeholders.cpf_cnpj')}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.cnpj}
                                                error={formik.touched.cnpj ? formik.errors.cnpj : ''}
                                            />
                                        </div>
                                        {/* A consulta é na base de pessoa jurídica, então não
                                            responde para CPF: o botão fica desabilitado em vez de
                                            sumir, para o operador entender que ele existe e por que
                                            não se aplica ao documento informado. */}
                                        <Button
                                            type="button"
                                            variant="outline-primary"
                                            onClick={handleLookupCnpj}
                                            disabled={isLookingUpCnpj || !isCnpjInformado}
                                            title={isCnpjInformado
                                                ? t('superadmin_company.lookup_cnpj')
                                                : t('superadmin_company.lookup_cnpj_only')}
                                        >
                                            {isLookingUpCnpj ? '...' : <i className="fas fa-search" />}
                                        </Button>
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>{t('trade_name')} <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="name"
                                        placeholder={t('superadmin_company.placeholders.name')}
                                        onChange={formik.handleChange}
                                        onBlur={handleNameBlur}
                                        value={formik.values.name}
                                        error={formik.touched.name ? formik.errors.name : ''}
                                        maxLength="50"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>{t('corporate_name')} <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="corporateName"
                                        placeholder={t('corporate_name_placeholder')}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.corporateName}
                                        error={formik.touched.corporateName ? formik.errors.corporateName : ''}
                                        maxLength="50"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel htmlFor="category">{t('category')} <AsteriskRequired /></FormLabel>
                                    {/* Segmento de atuação; define, no futuro, regras e telas por segmento. */}
                                    <CustomSelect
                                        inputId="category"
                                        name="category"
                                        options={categoryOptions}
                                        value={categoryOptions.find((option) => option.value === formik.values.category) || null}
                                        onChange={(option) => formik.setFieldValue('category', option?.value || '')}
                                        onBlur={() => formik.setFieldTouched('category', true)}
                                        error={formik.touched.category ? formik.errors.category : ''}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>{t('phone')}</FormLabel>
                                    <InputText
                                        name="phone"
                                        placeholder={t('superadmin_company.placeholders.phone')}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.phone}
                                        error={formik.touched.phone ? formik.errors.phone : ''}
                                        maxLength="30"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>{t('responsible_name')}</FormLabel>
                                    <InputText
                                        name="responsibleName"
                                        placeholder={t('responsible_name_placeholder')}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.responsibleName}
                                        error={formik.touched.responsibleName ? formik.errors.responsibleName : ''}
                                        maxLength="100"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Slug público</FormLabel>
                                    <InputText
                                        name="publicSlug"
                                        placeholder="minha-empresa"
                                        onChange={handlePublicSlugChange}
                                        onBlur={handlePublicSlugBlur}
                                        value={formik.values.publicSlug}
                                        error={formik.touched.publicSlug ? formik.errors.publicSlug : ''}
                                        maxLength="80"
                                    />
                                    {/* Se ficar vazio, o backend gera o slug a partir do nome da empresa. */}
                                    <small className="text-muted">Link: /p/{formik.values.publicSlug || 'slug-da-empresa'}</small>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>{t('superadmin_company.trial_days')}</FormLabel>
                                    <InputText
                                        name="trialDays"
                                        placeholder="Ex: 7"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.trialDays}
                                        error={formik.touched.trialDays ? formik.errors.trialDays : ''}
                                        maxLength="3"
                                        type="number"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </FormSection>

                    {isEditMode ? null : (
                        <FormSection
                            title="Primeira filial"
                            description="Cadastre a unidade inicial que ficará disponível assim que a empresa for criada."
                            icon="fa-solid fa-code-branch"
                        >
                            <Row className="g-3 mb-3">
                                <Col md={6}>
                                    <FormGroup>
                                        <FormLabel>{t('name')} <AsteriskRequired /></FormLabel>
                                        <InputText
                                            name="firstBranchName"
                                            placeholder="Ex.: Matriz"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.firstBranchName}
                                            error={formik.touched.firstBranchName ? formik.errors.firstBranchName : ''}
                                            maxLength="50"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <FormLabel>{t('cnpj')}</FormLabel>
                                        <CnpjInput
                                            name="firstBranchDocument"
                                            placeholder={t('document')}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.firstBranchDocument}
                                            error={formik.touched.firstBranchDocument ? formik.errors.firstBranchDocument : ''}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <h6 className="mb-3">{t('address.label')}</h6>
                            <AddressRegistration formik={formik} />
                        </FormSection>
                    )}

                    <FormSection title="Página pública" description="Configure até três etapas exibidas antes do agendamento público." icon="fa-solid fa-window-maximize">

                        <Row className="g-3">
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Quantidade de cards</FormLabel>
                                    <select
                                        name="publicIntroStepCount"
                                        className="form-control"
                                        value={formik.values.publicIntroStepCount}
                                        onChange={handlePublicIntroStepCountChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        <option value={0}>0 cards - ir direto para agendamento</option>
                                        <option value={1}>1 card</option>
                                        <option value={2}>2 cards</option>
                                        <option value={3}>3 cards</option>
                                    </select>
                                    <small className="text-muted d-block mt-2">
                                        Use 0 cards quando o cliente quiser ir direto para o agendamento público.
                                    </small>
                                    {formik.touched.publicIntroStepCount && formik.errors.publicIntroStepCount && (
                                        <div className="text-danger mt-1">{formik.errors.publicIntroStepCount}</div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <hr className="my-1" />
                            </Col>
                            {activePublicIntroStepCount === 0 && (
                                <Col md={12}>
                                    <Alert variant="info" className="mb-0">
                                        <i className="fa-solid fa-circle-info me-2" />
                                        Nenhum campo de card está liberado. A página pública vai enviar o cliente direto para o agendamento.
                                    </Alert>
                                </Col>
                            )}
                            {/* Renderiza as três etapas com o mesmo layout para reduzir duplicação. */}
                            {[1, 2, 3].filter((stepNumber) => stepNumber <= activePublicIntroStepCount).map((stepNumber) => {
                                const key = getPublicIntroStepKey(stepNumber);
                                const titleName = `publicIntroStep${key}Title`;
                                const textName = `publicIntroStep${key}Text`;
                                const selectedPreview = publicIntroImagePreviews[stepNumber];
                                const existingPreview = getExistingPublicIntroImageUrl(stepNumber);
                                const imagePreview = selectedPreview || existingPreview;

                                return (
                                    <React.Fragment key={stepNumber}>
                                        <Col md={12}>
                                            <h6 className="mb-0">Etapa {stepNumber}</h6>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <FormLabel>Título</FormLabel>
                                                <InputText
                                                    name={titleName}
                                                    placeholder={`Título da etapa ${stepNumber}`}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values[titleName]}
                                                    error={formik.touched[titleName] ? formik.errors[titleName] : ''}
                                                    maxLength="90"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <FormLabel>Foto</FormLabel>
                                                <div
                                                    className="superadmin-company-form__image-preview mb-2"
                                                    style={{ width: '100%', maxWidth: 360, height: 140, borderRadius: 8 }}
                                                >
                                                    {imagePreview ? (
                                                        <img
                                                            src={imagePreview}
                                                            alt={`Etapa ${stepNumber}`}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted px-3">
                                                            <i className="fa-solid fa-image fa-2x mb-2"></i>
                                                            <span>Sem foto</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    ref={publicIntroImageInputRefs[stepNumber]}
                                                    type="file"
                                                    className="form-control"
                                                    accept="image/png,image/jpeg,image/webp"
                                                    onChange={(event) => handlePublicIntroImageChange(stepNumber, event)}
                                                />
                                                <small className="text-muted d-block mt-2">
                                                    Formatos aceitos: PNG, JPG ou WEBP. Tamanho máximo: 5MB.
                                                </small>
                                                {isEditMode && existingPreview && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm mt-2"
                                                        onClick={() => removePublicIntroImage(stepNumber)}
                                                    >
                                                        <i className="fa-solid fa-trash me-2"></i>
                                                        Remover foto
                                                    </button>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={12}>
                                            <FormGroup>
                                                <FormLabel>Texto</FormLabel>
                                                <InputText
                                                    name={textName}
                                                    placeholder={`Texto da etapa ${stepNumber}`}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values[textName]}
                                                    error={formik.touched[textName] ? formik.errors[textName] : ''}
                                                    maxLength="260"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </React.Fragment>
                                );
                            })}
                        </Row>
                    </FormSection>

                    <FormSection title={t('status')} description={t('superadmin_company.status_hint')} icon="fa-solid fa-toggle-on" compact>

                        <Row>
                            <Col md={12}>
                                <FormGroup className="mb-0">
                                    <StatusCheckBox
                                        id="companyStatusCheckbox"
                                        name="status"
                                        defaultChecked={status}
                                        checked={status}
                                        onClick={() => setStatus(!status)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </FormSection>

                    {isEditMode ? null : (
                        <FormSection title={t('superadmin_company.admin_section')} description={t('superadmin_company.admin_hint')} icon="fa-solid fa-user-shield">

                            <Row className="g-3">
                                <Col md={6}>
                                    <FormGroup>
                                        <FormLabel>{t('first_name')} <AsteriskRequired /></FormLabel>
                                        <InputText
                                            name="adminFirstName"
                                            placeholder={t('superadmin_company.placeholders.admin_first_name')}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.adminFirstName}
                                            error={formik.touched.adminFirstName ? formik.errors.adminFirstName : ''}
                                            maxLength="20"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <FormLabel>{t('last_name')} <AsteriskRequired /></FormLabel>
                                        <InputText
                                            name="adminLastName"
                                            placeholder={t('superadmin_company.placeholders.admin_last_name')}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.adminLastName}
                                            error={formik.touched.adminLastName ? formik.errors.adminLastName : ''}
                                            maxLength="20"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <FormLabel>{t('email')} <AsteriskRequired /></FormLabel>
                                        <InputText
                                            name="adminEmail"
                                            placeholder={t('email_placeholder')}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.adminEmail}
                                            error={formik.touched.adminEmail ? formik.errors.adminEmail : ''}
                                            maxLength="50"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={12}>
                                    <Alert variant="info" className="mb-0">
                                        <i className="fa-solid fa-circle-info me-2" />
                                        {t('superadmin_company.first_access_email_hint')}
                                    </Alert>
                                </Col>
                            </Row>
                        </FormSection>
                    )}
                </div>
            }
        />
    );
};

export default CompanyProvisionNew;
